import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { Android, Download } from '@mui/icons-material';
import { useGetMobileReleaseQuery } from '../../../services/MobileReleaseService';

/**
 * Internal Android install page. Replaces the standalone Static Web App that previously served
 * this content behind Static Web Apps edge auth — see specs/009-trackly-mobile-app/research.md
 * item 16. The page markup is anonymously reachable, but carries no secret: the download link only
 * ever arrives from a bearer-token-authenticated API call.
 */
function GetTheAppPage() {
  const { data, isLoading, isError } = useGetMobileReleaseQuery('android');

  return (
    <Box sx={{ maxWidth: 680, width: '100%', p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Android sx={{ color: '#3ddc84' }} />
        <Typography variant="h5" fontWeight={600}>
          Trackly Mobile — Internal Install
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        This page is for Trackly employees only. Do not share this link outside the organization.
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          {isLoading && <Typography>Loading the current build…</Typography>}

          {(isError || (!isLoading && !data)) && (
            <Typography>
              No build is currently published for this environment. Contact the Trackly engineering
              team.
            </Typography>
          )}

          {data && (
            <Stack spacing={1.5} alignItems="flex-start">
              <Typography>Version {data.version}</Typography>
              <Button
                variant="contained"
                startIcon={<Download />}
                href={data.downloadUrl}
                data-testid="download-apk"
              >
                Download Trackly Mobile (Android)
              </Button>
              <Typography variant="body2" color="text.secondary">
                Released {new Date(data.releasedAt).toLocaleString()}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Expect a Google Play Protect warning.</strong> This app is installed directly, not
        through the Play Store, so Android will warn you before installing it (some phones, notably
        Samsung and Xiaomi, add extra prompts). This is expected — choose "Install anyway"/"Keep
        app".
      </Alert>

      <Card variant="outlined">
        <CardContent>
          <Typography fontWeight={600} gutterBottom>
            Install steps
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
            <li>Tap the download button above to fetch the APK.</li>
            <li>
              If prompted, allow installs from this source (Settings → Apps → Special access →
              Install unknown apps → allow for your browser/files app).
            </li>
            <li>Open the downloaded file and tap Install.</li>
            <li>Open Trackly and sign in with your work account.</li>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default GetTheAppPage;
